import { animated, useSpring } from '@react-spring/three';
import { useGLTF } from '@react-three/drei';
import React, { useContext, useEffect, useMemo } from 'react';
import { TextureLoader } from 'three';
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosFront,
  Deg0,
  DoorDeg90CCW,
  HandleDeg45CW,
  Scale0,
  Scale1,
} from '../../utils/AnimaionSprings';
import CurrentContext from '../../utils/CurrentContext';
import useChangedTexture from '../../utils/customHooks/useChangedTexture';
import useTextureCustom from '../../utils/customHooks/useTextureCustom';
import {
  BlackPlastic,
  BoltMaterial,
  PaintMaterial,
} from '../../utils/finsishesData';
import { handleExportGLTF } from '../../utils/jsHelper';
import { LockSecuramToplit } from './model_locks';
import useIsMobile from '../../utils/customHooks/useIsMobile';

//object to store default finish of the prop
const defaultoptions = [
  {
    materialName: 'body_paint',
    finishToApply: 'texturedblack',
  },
  {
    materialName: 'handle_body',
    finishToApply: 'vaultdoorblockade_handle_body',
  },
  {
    materialName: 'toplit_elock_body',
    finishToApply: 'vaultdoorblockade_toplit_elock_body',
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function VaultBlockade({ animateInit, currentVariant, lockType }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile
      ? 'models/vaultdoorblockade-m.glb'
      : 'models/vaultdoorblockade.glb',
  );
  const mobileModel = useGLTF('models/vaultdoorblockade-m.glb');

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          lockType,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
            lockType,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            lockType,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
    // currentVariant,
  } = useContext(CurrentContext);

  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg45CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg45CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: 'vaultdoorblockade',
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR, mobileProp]);

  const loader = useMemo(() => new TextureLoader(), []);

  const decalDoorLogo = useMemo(
    () => loader.load('textures/decal_door_vaultdoorblockade.png'),
    [],
  );
  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    outer_materials: 'door_main',
    hardware_materials: ['handle_body', 'wheel_handle', 'toplit_elock_body'],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture,
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const decalOuterColor =
    currentVariant.hardware === 'blackchromehardware'
      ? '#828181'
      : currentVariant.hardware === 'chromehardware'
      ? '#e5e5e5'
      : currentVariant.hardware === 'brasshardware'
      ? '#C1A163'
      : '#828181';

  return (
    <group
      dispose={null}
      scale={[currentVariant.Hinge === 'left' ? 1 : -1, 1, 1]}
      position={[currentVariant.Hinge === 'left' ? -0.565 : 0.565, 0, -0.15]}
      visible={visible}
    >
      <animated.group position={[0.146, 0.022, 0.237]} rotation={rotation_Door}>
        <animated.mesh
          castShadow
          geometry={nodes.bolt_front.geometry}
          material={BoltMaterial}
          position={position_Bolt_Back}
        />
        <mesh
          castShadow
          geometry={nodes.bolts_housing.geometry}
          material={BlackPlastic}
        />
        <mesh
          castShadow
          geometry={nodes.door_parts.geometry}
          material={materials.door_screws_chrome}
        />
        <mesh
          castShadow
          geometry={nodes.handle_body.geometry}
          material={materials.handle_body}
        />
        <mesh
          castShadow
          geometry={nodes.hinges.geometry}
          material={materials.door_hinges}
        />

        {/* Locks */}
        <group position={[0.614, 1.298, 0.018]}>
          <LockSecuramToplit material={materials.toplit_elock_body} />
        </group>
        {/* Door exterior */}
        <group position={[0.594, 1.119, -0.022]}>
          <mesh
            castShadow
            geometry={nodes.outer_door.geometry}
            material={PaintMaterial}
          />
          <mesh
            position={[
              currentVariant.Hinge === 'left' ? -0.315 : 0,
              -0.125,
              0.05,
            ]}
            scale={[currentVariant.Hinge === 'left' ? 2.85 : -2.85, 2.85, 1]}
            // scale={[scale/100, scale/100, 1]}
          >
            <planeGeometry args={[0.7, 0.7]} />
            <meshBasicMaterial
              color={decalOuterColor}
              map={decalDoorLogo}
              transparent={true}
            />
          </mesh>
        </group>

        <animated.group
          position={[0.614, 1.123, 0.072]}
          rotation={handle_Rotation1}
          scale={handle_Scale1}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4_1.geometry}
            material={materials.Wheel_Decal}
          />
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4_2.geometry}
            material={materials.Handle_Plane}
          />
        </animated.group>
        <animated.group
          position={[0.614, 1.123, 0.072]}
          rotation={handle_Rotation2}
          scale={handle_Scale2}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4_1.geometry}
            material={materials.Wheel_Decal}
          />
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.Mesh_4_2.geometry}
            material={materials.Handle_Plane}
          />
        </animated.group>
      </animated.group>
      <mesh
        castShadow
        geometry={nodes.screws_bodycolor.geometry}
        material={materials.screws_bodycolor}
      />
      <mesh
        castShadow
        geometry={nodes.outer_body.geometry}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.screws.geometry}
        material={materials.screws_chrome}
        position={[0.566, 1.08, 0.146]}
      />
    </group>
  );
};

//useGLTF.preload("models/vaultdoorblockade.glb");
